import { GroupTypes } from "components/Risk/types";
import { calculateScore } from "components/Risk/utils";
import { RiskGroup } from "pages/Risk/hooks/useRiskGroups";

import {
  AIDraftMitigation,
  AIDraftRisk,
  DraftIssue,
  DraftValidation,
} from "./types";

/**
 * Pure ISO 12100 draft validator (plan §8). Runs after every generation
 * (display in WarningsPanel) and again at accept time (Accept Gate).
 *
 * HARD ERRORS (block accept — `risks.ai.invalid.*`):
 * | code                    | i18nKey                            | section        | params                            |
 * |-------------------------|------------------------------------|----------------|-----------------------------------|
 * | vocab_group             | risks.ai.invalid.vocabGroup        | risk           | { value }                         |
 * | vocab_origin            | risks.ai.invalid.vocabOrigin       | risk           | { value }                         |
 * | vocab_consequence       | risks.ai.invalid.vocabConsequence  | risk           | { values }                        |
 * | vocab_phase             | risks.ai.invalid.vocabPhase        | risk           | { values }                        |
 * | param_out_of_range      | risks.ai.invalid.range             | risk/mitig.    | { index?, field, value, min, max }|
 * | no_mitigations          | risks.ai.invalid.noMitigations     | risk           | —                                 |
 * | invalid_mitigation_type | risks.ai.invalid.invalidType       | mitigation_<i> | { index, value }                  |
 * | pictogram_missing       | risks.ai.invalid.pictogramMissing  | mitigation_<i> | { index }                         |
 *
 * WARNINGS (require explicit acknowledgment — `risks.ai.warnings.*`):
 * | code                       | i18nKey                            | section        | params                                      |
 * |----------------------------|------------------------------------|----------------|---------------------------------------------|
 * | hierarchy_violated         | risks.ai.warnings.hierarchy        | mitigation_<i> | { index, type }                             |
 * | ems_ess_shared_iteration   | risks.ai.warnings.separation       | mitigation_<i> | { index }                                   |
 * | pik_without_bda            | risks.ai.warnings.pikFollowup      | mitigation_<i> | { index }                                   |
 * | iteration_count_out_of_bounds | risks.ai.warnings.iterations    | risk           | { count, min, max }                         |
 * | param_increased            | risks.ai.warnings.monotonic        | mitigation_<i> | { index, fields, previousScore, score }     |
 * | reasoning_missing          | risks.ai.warnings.reasoningMissing | risk/mitig.    | { index? }                                  |
 * | left_risk_missing          | risks.ai.warnings.leftRiskMissing  | mitigation_<i> | { index }                                   |
 * | standards_unknown          | risks.ai.warnings.standardsUnknown | mitigation_<i> | { index, standards }                        |
 *
 * Conventions: `section` carries the 0-based mitigation array index
 * (programmatic mapping in WarningsPanel); `params.index` is 1-based for
 * human display. Nothing is ever silently auto-fixed — issues are surfaced
 * and either block accept (errors) or demand acknowledgment (warnings).
 */

// The 5 assessment params shared by risk + every mitigation iteration.
const PARAM_FIELDS = [
  "impact",
  "people_count",
  "duration",
  "detectability",
  "probability",
] as const;

type ParamField = (typeof PARAM_FIELDS)[number];
type ParamCarrier = Record<ParamField, number>;

// Allowed integer ranges, mirroring the form options in
// src/components/Risk/formdata.tsx (impact 1-4, people_count 1-3,
// duration 1-4, detectability 1-3, probability 1-5).
export const RISK_PARAM_RANGES: Record<ParamField, { min: number; max: number }> = {
  impact: { min: 1, max: 4 },
  people_count: { min: 1, max: 3 },
  duration: { min: 1, max: 4 },
  detectability: { min: 1, max: 3 },
  probability: { min: 1, max: 5 },
};

// The 13 hardcoded life phases — values mirror the phase_id multiSelect
// options in src/components/Risk/formdata.tsx (note: "operations", not
// "operation"). Kept in sync via validateDraft.test.ts.
export const RISK_PHASE_IDS = [
  "transport",
  "installation",
  "commissioning",
  "setup",
  "adapt",
  "operations",
  "disturbance",
  "debugging",
  "maintenance",
  "cleaning",
  "decommissioning",
  "deinstallation",
  "disposal",
];

// Mitigation type vocabulary — mirrors the type select options in
// src/components/Risk/formdata.tsx. Kept in sync via validateDraft.test.ts.
export const MITIGATION_TYPES = ["INH", "EMS", "ESS", "PSA", "PIK", "BDA"];

// ISO 12100 measure hierarchy: Cat 1 (inherently safe design) before
// Cat 2 (safeguarding) before Cat 3 (information for use).
const MITIGATION_CATEGORY: Record<string, number> = {
  INH: 1,
  EMS: 2,
  ESS: 2,
  PIK: 3,
  BDA: 3,
  PSA: 3,
};

const ITERATION_MIN = 2;
const ITERATION_MAX = 5;

// GroupTypes is a numeric enum — its non-numeric keys are the group names.
export const GROUP_TYPE_NAMES = Object.keys(GroupTypes).filter((key) =>
  Number.isNaN(Number(key))
);

// consequence_id / phase_id wire format: SEMICOLON-joined value strings
// (pinned in api_contracts.md). Empty selection = "" — emptiness is the
// form's required-check, not a vocabulary violation.
const splitMultiValue = (value: string | undefined): string[] =>
  (value || "").split(";").filter((part) => part !== "");

const isBlank = (value: string | undefined): boolean =>
  !value || !value.trim();

const mitigationSection = (index: number): DraftIssue["section"] =>
  `mitigation_${index}` as `mitigation_${number}`;

const checkParamRanges = (
  carrier: ParamCarrier,
  section: DraftIssue["section"],
  displayIndex: number | undefined,
  errors: DraftIssue[]
) => {
  PARAM_FIELDS.forEach((field) => {
    const value = carrier[field];
    const { min, max } = RISK_PARAM_RANGES[field];

    if (!Number.isInteger(value) || value < min || value > max) {
      errors.push({
        code: "param_out_of_range",
        i18nKey: "risks.ai.invalid.range",
        section,
        params:
          displayIndex === undefined
            ? { field, value, min, max }
            : { index: displayIndex, field, value, min, max },
      });
    }
  });
};

const checkClassificationVocabulary = (
  draft: AIDraftRisk,
  riskGroups: RiskGroup[],
  errors: DraftIssue[]
) => {
  if (!GROUP_TYPE_NAMES.includes(draft.group_id)) {
    errors.push({
      code: "vocab_group",
      i18nKey: "risks.ai.invalid.vocabGroup",
      section: "risk",
      params: { value: draft.group_id },
    });
  } else {
    // origin/consequence membership is defined per chosen group; with an
    // unknown group there is no vocabulary to check against (the group
    // error above already blocks accept). A group that is valid per
    // GroupTypes but missing from the /risk-groups payload means the
    // vocabulary data — not the draft — is incomplete: no error fabricated.
    const group = riskGroups.find(
      (riskGroup) => riskGroup.riskGroup.name === draft.group_id
    );

    if (group) {
      if (!group.origins.some((origin) => origin.name === draft.origin_id)) {
        errors.push({
          code: "vocab_origin",
          i18nKey: "risks.ai.invalid.vocabOrigin",
          section: "risk",
          params: { value: draft.origin_id },
        });
      }

      const unknownConsequences = splitMultiValue(draft.consequence_id).filter(
        (value) =>
          !group.consequences.some((consequence) => consequence.name === value)
      );

      if (unknownConsequences.length) {
        errors.push({
          code: "vocab_consequence",
          i18nKey: "risks.ai.invalid.vocabConsequence",
          section: "risk",
          params: { values: unknownConsequences.join(", ") },
        });
      }
    }
  }

  const unknownPhases = splitMultiValue(draft.phase_id).filter(
    (value) => !RISK_PHASE_IDS.includes(value)
  );

  if (unknownPhases.length) {
    errors.push({
      code: "vocab_phase",
      i18nKey: "risks.ai.invalid.vocabPhase",
      section: "risk",
      params: { values: unknownPhases.join(", ") },
    });
  }
};

const checkMitigations = (
  draft: AIDraftRisk,
  projectStandards: string[],
  errors: DraftIssue[],
  warnings: DraftIssue[]
) => {
  const mitigations = draft.mitigations || [];
  const knownStandards = projectStandards || [];

  if (mitigations.length === 0) {
    errors.push({
      code: "no_mitigations",
      i18nKey: "risks.ai.invalid.noMitigations",
      section: "risk",
    });

    return;
  }

  if (mitigations.length < ITERATION_MIN || mitigations.length > ITERATION_MAX) {
    warnings.push({
      code: "iteration_count_out_of_bounds",
      i18nKey: "risks.ai.warnings.iterations",
      section: "risk",
      params: {
        count: mitigations.length,
        min: ITERATION_MIN,
        max: ITERATION_MAX,
      },
    });
  }

  let highestCategorySoFar = 0;

  mitigations.forEach((mitigation: AIDraftMitigation, index: number) => {
    const section = mitigationSection(index);
    const displayIndex = index + 1;

    // --- hard errors ---------------------------------------------------
    if (!MITIGATION_TYPES.includes(mitigation.type)) {
      errors.push({
        code: "invalid_mitigation_type",
        i18nKey: "risks.ai.invalid.invalidType",
        section,
        params: { index: displayIndex, value: mitigation.type },
      });
    }

    checkParamRanges(mitigation, section, displayIndex, errors);

    if (mitigation.type === "PIK" && isBlank(mitigation.pictogram)) {
      errors.push({
        code: "pictogram_missing",
        i18nKey: "risks.ai.invalid.pictogramMissing",
        section,
        params: { index: displayIndex },
      });
    }

    // --- warnings -------------------------------------------------------
    // Hierarchy: INH < EMS/ESS < PIK/BDA/PSA — a measure must never follow
    // one from a lower-priority (= higher-numbered) category.
    const category = MITIGATION_CATEGORY[mitigation.type];

    if (category !== undefined) {
      if (category < highestCategorySoFar) {
        warnings.push({
          code: "hierarchy_violated",
          i18nKey: "risks.ai.warnings.hierarchy",
          section,
          params: { index: displayIndex, type: mitigation.type },
        });
      }

      highestCategorySoFar = Math.max(highestCategorySoFar, category);
    }

    // EMS/ESS must be assessed in SEPARATE iterations. Detectable as
    // (a) a compound type trying to express both in one iteration, or
    // (b) adjacent EMS/ESS iterations with identical residual params —
    //     i.e. no separate residual-risk estimation took place.
    const isCompoundEmsEss =
      /\bEMS\b/.test(mitigation.type) && /\bESS\b/.test(mitigation.type);
    const previousMitigation = index > 0 ? mitigations[index - 1] : undefined;
    const isUnseparatedPair =
      previousMitigation !== undefined &&
      ((mitigation.type === "ESS" && previousMitigation.type === "EMS") ||
        (mitigation.type === "EMS" && previousMitigation.type === "ESS")) &&
      PARAM_FIELDS.every(
        (field) => mitigation[field] === previousMitigation[field]
      );

    if (isCompoundEmsEss || isUnseparatedPair) {
      warnings.push({
        code: "ems_ess_shared_iteration",
        i18nKey: "risks.ai.warnings.separation",
        section,
        params: { index: displayIndex },
      });
    }

    // A PIK (pictogram) iteration must be followed by a BDA (user
    // information) iteration (EN ISO 20607).
    if (
      mitigation.type === "PIK" &&
      !mitigations
        .slice(index + 1)
        .some((following) => following.type === "BDA")
    ) {
      warnings.push({
        code: "pik_without_bda",
        i18nKey: "risks.ai.warnings.pikFollowup",
        section,
        params: { index: displayIndex },
      });
    }

    // Monotonicity: none of the 5 params may increase vs the previous
    // iteration (baseline = the risk assessment itself).
    const previous: ParamCarrier = previousMitigation || draft;
    const increasedFields = PARAM_FIELDS.filter(
      (field) => mitigation[field] > previous[field]
    );

    if (increasedFields.length) {
      warnings.push({
        code: "param_increased",
        i18nKey: "risks.ai.warnings.monotonic",
        section,
        params: {
          index: displayIndex,
          fields: increasedFields.join(", "),
          previousScore: calculateScore(previous),
          score: calculateScore(mitigation),
        },
      });
    }

    if (isBlank(mitigation.reasoning)) {
      warnings.push({
        code: "reasoning_missing",
        i18nKey: "risks.ai.warnings.reasoningMissing",
        section,
        params: { index: displayIndex },
      });
    }

    if (isBlank(mitigation.left_risk)) {
      warnings.push({
        code: "left_risk_missing",
        i18nKey: "risks.ai.warnings.leftRiskMissing",
        section,
        params: { index: displayIndex },
      });
    }

    const unknownStandards = (mitigation.applied_standards || []).filter(
      (standard) => !knownStandards.includes(standard)
    );

    if (unknownStandards.length) {
      warnings.push({
        code: "standards_unknown",
        i18nKey: "risks.ai.warnings.standardsUnknown",
        section,
        params: { index: displayIndex, standards: unknownStandards.join(", ") },
      });
    }
  });
};

export const validateDraft = (
  draft: AIDraftRisk,
  riskGroups: RiskGroup[],
  projectStandards: string[]
): DraftValidation => {
  const errors: DraftIssue[] = [];
  const warnings: DraftIssue[] = [];

  checkClassificationVocabulary(draft, riskGroups || [], errors);
  checkParamRanges(draft, "risk", undefined, errors);

  if (isBlank(draft.reasoning)) {
    warnings.push({
      code: "reasoning_missing",
      i18nKey: "risks.ai.warnings.reasoningMissing",
      section: "risk",
    });
  }

  checkMitigations(draft, projectStandards, errors, warnings);

  return { errors, warnings };
};
